import { create } from 'zustand';
import type { Numerals } from '@shared/lib/i18n/locales';

export type ThemePreference = 'light' | 'dark' | 'system';

const THEME_KEY = 'tb.theme';

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function readTheme(): ThemePreference {
  try {
    const value = window.localStorage.getItem(THEME_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Applies the theme exactly as tokens.css expects: explicit [data-theme] or none for "system". */
export function applyTheme(theme: ThemePreference): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

interface PreferencesState {
  theme: ThemePreference;
  /** Digits used by Intl formatters (users.numerals). */
  numerals: Numerals;
  /** Viewer's IANA timezone: the profile's timezone once signed in, the browser's before. */
  timeZone: string;
  setTheme: (theme: ThemePreference) => void;
  setNumerals: (numerals: Numerals) => void;
  setTimeZone: (timeZone: string) => void;
}

export const usePreferences = create<PreferencesState>((set) => ({
  theme: typeof window === 'undefined' ? 'system' : readTheme(),
  numerals: 'latn',
  timeZone: browserTimeZone(),
  setTheme: (theme) => {
    try {
      if (theme === 'system') window.localStorage.removeItem(THEME_KEY);
      else window.localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore storage errors */
    }
    applyTheme(theme);
    set({ theme });
  },
  setNumerals: (numerals) => {
    document.documentElement.setAttribute('data-numerals', numerals);
    set({ numerals });
  },
  setTimeZone: (timeZone) => set({ timeZone }),
}));
